import supabase from './supabase';
import {
  StaffPosition,
  StaffPositionRow,
  StaffPositionType,
  POSITION_TYPE_LABELS,
} from '../utils/positionTypes';

function transformPositionRow(row: StaffPositionRow): StaffPosition {
  return {
    id: row.id,
    staffId: row.staff_id,
    staffName: row.profiles?.display_name || row.profiles?.email || 'Unknown',
    staffEmail: row.profiles?.email || '',
    positionType: row.position_type,
    yearLevel: row.year_level,
    assignedAt: row.assigned_at,
    assignedBy: row.assigned_by,
    isActive: row.is_active,
  };
}

export async function getStaffPositions(filters?: {
  positionType?: StaffPositionType;
  staffId?: string;
  activeOnly?: boolean;
}): Promise<StaffPosition[]> {
  let query = supabase
    .from('staff_positions')
    .select(`
      id,
      staff_id,
      position_type,
      year_level,
      assigned_at,
      assigned_by,
      is_active,
      profiles!staff_positions_staff_id_fkey (
        id,
        display_name,
        email
      )
    `)
    .order('position_type')
    .order('year_level', { nullsFirst: true });

  if (filters?.positionType) {
    query = query.eq('position_type', filters.positionType);
  }
  if (filters?.staffId) {
    query = query.eq('staff_id', filters.staffId);
  }
  if (filters?.activeOnly !== false) {
    query = query.eq('is_active', true);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching staff positions:', error);
    throw new Error('Failed to fetch staff positions');
  }

  return (data || []).map((row) => transformPositionRow(row as unknown as StaffPositionRow));
}

export async function getStaffByPosition(
  positionType: StaffPositionType,
  yearLevel?: number
): Promise<StaffPosition[]> {
  let query = supabase
    .from('staff_positions')
    .select(`
      id,
      staff_id,
      position_type,
      year_level,
      assigned_at,
      assigned_by,
      is_active,
      profiles!staff_positions_staff_id_fkey (
        id,
        display_name,
        email
      )
    `)
    .eq('position_type', positionType)
    .eq('is_active', true);

  if (yearLevel !== undefined) {
    query = query.eq('year_level', yearLevel);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching staff by position:', error);
    throw new Error('Failed to fetch staff by position');
  }

  return (data || []).map((row) => transformPositionRow(row as unknown as StaffPositionRow));
}

export async function assignPosition(
  staffId: string,
  positionType: StaffPositionType,
  yearLevel?: number,
  assignedBy?: string
): Promise<string> {
  const { data, error } = await supabase
    .from('staff_positions')
    .insert({
      staff_id: staffId,
      position_type: positionType,
      year_level: positionType === 'yll_l' ? yearLevel : null,
      assigned_by: assignedBy || null,
      is_active: true,
    })
    .select('id')
    .single();

  if (error) {
    if (error.code === '23505') {
      throw new Error('This staff member already has this position');
    }
    console.error('Error assigning position:', error);
    throw new Error('Failed to assign position');
  }

  return data.id;
}

export async function removePosition(positionId: string): Promise<void> {
  const { error } = await supabase
    .from('staff_positions')
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq('id', positionId);

  if (error) {
    console.error('Error removing position:', error);
    throw new Error('Failed to remove position');
  }
}

export async function hasPosition(
  staffId: string,
  positionType: StaffPositionType
): Promise<boolean> {
  const { data, error } = await supabase
    .from('staff_positions')
    .select('id')
    .eq('staff_id', staffId)
    .eq('position_type', positionType)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    console.error('Error checking position:', error);
    return false;
  }

  return !!data;
}

export async function getStaffPositionTypes(staffId: string): Promise<StaffPositionType[]> {
  const { data, error } = await supabase
    .from('staff_positions')
    .select('position_type')
    .eq('staff_id', staffId)
    .eq('is_active', true);

  if (error) {
    console.error('Error fetching staff position types:', error);
    return [];
  }

  return (data || []).map((row) => row.position_type as StaffPositionType);
}

export { POSITION_TYPE_LABELS };
